import { Component, OnInit, OnDestroy, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ServiceCategoryService, ServiceCategory } from '../../../services/service-category';
import { AdminHeader } from '../../../services/admin-header';
import { environment } from '../../../../environments/environment';

@Component({
	selector: 'app-service-categories',
	imports: [CommonModule, ReactiveFormsModule],
	templateUrl: './service-categories.html',
	styleUrl: './service-categories.css',
})
export class ServiceCategories implements OnInit, OnDestroy {
	private categoryService = inject(ServiceCategoryService);
	private fb = inject(FormBuilder);
	private adminHeader = inject(AdminHeader);

	categories = signal<ServiceCategory[]>([]);
	isLoading = signal(true);
	showModal = signal(false);
	isEditMode = signal(false);
	isSubmitting = signal(false);
	errorMessage = signal('');
	selectedCategory = signal<ServiceCategory | null>(null);
	selectedImage = signal<File | null>(null);
	imagePreview = signal<string | null>(null);
	storageUrl = environment.storageUrl;

	categoryForm: FormGroup = this.fb.group({
		name: ['', Validators.required],
		description: [''],
		icon: [''],
		display_order: [0],
		is_active: [true],
	});

	ngOnInit() {
		this.loadCategories();
		this.adminHeader.setHeaderAction({
			label: 'Add Category',
			icon: 'M12 6v6m0 0v6m0-6h6m-6 0H6',
			callback: () => this.openModal(),
		});
	}

	ngOnDestroy() {
		this.adminHeader.clearHeaderAction();
	}

	loadCategories() {
		this.isLoading.set(true);
		this.categoryService.getAll().subscribe({
			next: (res) => {
				if (res.status) this.categories.set(res.data);
				this.isLoading.set(false);
			},
			error: () => this.isLoading.set(false),
		});
	}

	openModal(category?: ServiceCategory) {
		if (category) {
			this.isEditMode.set(true);
			this.selectedCategory.set(category);
			this.categoryForm.patchValue(category);
			this.imagePreview.set(category.image ? `${this.storageUrl}/${category.image}` : null);
		} else {
			this.isEditMode.set(false);
			this.selectedCategory.set(null);
			this.categoryForm.reset({ display_order: 0, is_active: true });
			this.imagePreview.set(null);
		}
		this.selectedImage.set(null);
		this.showModal.set(true);
	}

	closeModal() {
		this.showModal.set(false);
		this.categoryForm.reset();
		this.errorMessage.set('');
		this.selectedImage.set(null);
		this.imagePreview.set(null);
	}

	onImageSelect(event: Event) {
		const input = event.target as HTMLInputElement;
		if (input.files && input.files[0]) {
			const file = input.files[0];

			// Validate file size (5MB max)
			if (file.size > 5 * 1024 * 1024) {
				alert('Image size must be less than 5MB');
				input.value = '';
				return;
			}

			this.selectedImage.set(file);

			// Create preview
			const reader = new FileReader();
			reader.onload = (e) => {
				this.imagePreview.set(e.target?.result as string);
			};
			reader.readAsDataURL(file);
		}
	}

	removeImage() {
		this.selectedImage.set(null);
		this.imagePreview.set(null);
	}

	saveCategory() {
		if (this.categoryForm.invalid) return;

		this.isSubmitting.set(true);
		const formData = new FormData();
		const formValue = this.categoryForm.value;

		// Append form fields
		formData.append('name', formValue.name || '');
		formData.append('description', formValue.description || '');
		formData.append('icon', formValue.icon || '');
		formData.append('display_order', String(formValue.display_order || 0));
		formData.append('is_active', formValue.is_active ? '1' : '0');

		// Append image if selected
		if (this.selectedImage()) {
			formData.append('image', this.selectedImage()!);
		}

		const request = this.isEditMode()
			? this.categoryService.update(this.selectedCategory()!.id, formData)
			: this.categoryService.create(formData);

		request.subscribe({
			next: () => {
				this.loadCategories();
				this.closeModal();
				this.isSubmitting.set(false);
			},
			error: (err) => {
				this.errorMessage.set(err.error?.message || 'Failed to save category');
				this.isSubmitting.set(false);
			},
		});
	}

	deleteCategory(category: ServiceCategory) {
		if (!confirm(`Delete "${category.name}"? This cannot be undone.`)) return;

		this.categoryService.delete(category.id).subscribe({
			next: () => this.loadCategories(),
			error: (err) => alert(err.error?.message || 'Cannot delete category with services'),
		});
	}
}
